
"use client"

import { AppSystemColorType, systemModes, SystemModeType } from "@/theme"
import { ChakraProvider } from "@chakra-ui/react";
import { createContext, useContext, useEffect, useMemo, useState } from "react"

type ThemeSystemContextValue = {
  mode: SystemModeType,
  system: AppSystemColorType,
  setMode: (mode: SystemModeType) => void
}

const ThemeSystemContext = createContext<ThemeSystemContextValue | undefined>(undefined);

export const ThemeSystemProvider = (props: { children: React.ReactNode }) => {
  
  const [mode, setMode] = useState<SystemModeType>("light");
  const [isLoaded, setIsLoaded] = useState(false);
  
  useEffect(() => {
    const storedType = localStorage.getItem('meshr-cached-theme') as SystemModeType | null;
    
    if (storedType && systemModes[storedType]) {
      setMode(storedType);
      setIsLoaded(true);
    }
  }, []);
  
  useEffect(() => {
    if (isLoaded) {
      localStorage.setItem('meshr-cached-theme', mode);
    }
  }, [mode]);
  
  const system = useMemo(() => systemModes[mode] ?? systemModes.light, [mode]);
  
  const value = useMemo(() => ({ mode, system, setMode }), [mode, system]);
  
  return (
    <ThemeSystemContext.Provider value={value}>
      <ChakraProvider value={system}>
        {props.children}
      </ChakraProvider>
    </ThemeSystemContext.Provider>
  );
  
}

export const useSystemTheme = () => {
  const context = useContext(ThemeSystemContext);
  
  if (!context) {
    throw new Error('useSystemTheme must be used inside ThemeSystemProvider');
  }
  
  return context;
}